'use client';
import { jsx, jsxs } from "react/jsx-runtime";
import { useOnClickOutside, cn } from "@tameeni/shared-logic";
import React__default, { useState, useRef } from "react";
import ReactSelect, { components } from "react-select";
import { Icon } from "@tameeni/icons";
import { CustomInput, CustomOption, getClassNames } from "./components.js";
const MultiSelect = React__default.forwardRef(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (props, ref) => {
    const {
      value,
      options,
      className,
      onChange,
      onClear,
      placeholder,
      isInvalid = false,
      isDisabled,
      isLoading,
      isSearchable,
      listHeading,
      styles,
      components: customComponents,
      // Extract our custom props
      ...restSelectProps
      // All remaining props will be passed to ReactSelect
    } = props;
    const [isSelected, setIsSelected] = useState(value && value.length > 0);
    const [isMenuOpened, setIsMenuOpened] = useState(false);
    const clickOutsideRef = useRef(null);
    useOnClickOutside(clickOutsideRef, () => setIsMenuOpened(false));
    const selectedValue = React__default.useMemo(() => {
      return options.filter((item) => value == null ? void 0 : value.includes(item.value));
    }, [value, options]);
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref: clickOutsideRef,
        onClick: () => setIsMenuOpened(true),
        className: cn(className),
        children: /* @__PURE__ */ jsx(
          ReactSelect,
          {
            menuIsOpen: isMenuOpened,
            onFocus: () => setIsMenuOpened(true),
            onKeyDown: (e) => {
              if (e.key === "Escape") {
                setIsMenuOpened(false);
              }
              if (e.key === "Enter" || e.key === " ") {
                setIsMenuOpened(true);
              }
            },
            options,
            unstyled: true,
            ref,
            value: selectedValue,
            noOptionsMessage: () => "No options",
            placeholder: placeholder || "",
            closeMenuOnSelect: false,
            hideSelectedOptions: false,
            isMulti: true,
            isClearable: true,
            menuShouldScrollIntoView: true,
            openMenuOnFocus: true,
            isDisabled,
            isLoading,
            isSearchable,
            onChange: (newValue, meta) => {
              if (meta.action === "clear") {
                onClear == null ? void 0 : onClear();
              } else if (newValue) {
                onChange == null ? void 0 : onChange(
                  newValue.map(
                    (item) => item.value
                  )
                );
                if ((newValue == null ? void 0 : newValue.length) > 0) setIsSelected(true);
                else setIsSelected(false);
              }
            },
            classNames: getClassNames({
              isInvalid,
              isMenuOpened,
              isSelected: false
            }),
            styles: {
              ...styles,
              menuPortal: (base, _props) => ({
                ...base,
                pointerEvents: "auto",
                zIndex: 9999
              })
            },
            components: {
              Option: CustomOption,
              Input: CustomInput,
              Menu: ({ children, className: className2, ...props2 }) => /* @__PURE__ */ jsxs(components.Menu, { ...props2, children: [
                listHeading && /* @__PURE__ */ jsx("div", { className: "text-sm font-semibold text-slate-400 px-3 py-2", children: listHeading }),
                children
              ] }),
              ClearIndicator: () => isSelected && /* @__PURE__ */ jsx(Icon, { name: "CheckCircle", className: "size-4 text-green" }),
              ...customComponents
            },
            ...restSelectProps
          },
          `react_select_key_${selectedValue.map((item) => item.value).join(",")}`
        )
      }
    );
  }
);
MultiSelect.displayName = "MultiSelect";
export {
  MultiSelect
};
